import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { formatDateToIso } from "../../../utils/availability";
import "./DateTimePicker.css";

interface DateTimePickerProps {
  /** Local value in "YYYY-MM-DDTHH:mm" format (same as datetime-local), or "" */
  value: string;
  onChange: (value: string) => void;
  /** Dates before this day cannot be picked */
  disabledBefore?: Date;
  required?: boolean;
}

const pad2 = (n: number) => String(n).padStart(2, "0");

const TIME_OPTIONS: string[] = (() => {
  const options: string[] = [];
  for (let h = 0; h < 24; h += 1) {
    options.push(`${pad2(h)}:00`, `${pad2(h)}:30`);
  }
  return options;
})();

function DateTimePicker({ value, onChange, disabledBefore, required }: DateTimePickerProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.resolvedLanguage?.startsWith("ar") ?? false;
  const [calendarOpen, setCalendarOpen] = useState(false);

  const parsed = useMemo(() => {
    if (!value) return { date: "", time: "" };
    const [date = "", time = ""] = value.split("T");
    return { date, time };
  }, [value]);

  const selectedDay = useMemo(() => {
    if (!parsed.date) return undefined;
    const d = new Date(`${parsed.date}T00:00:00`);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [parsed.date]);

  const displayLabel = useMemo(() => {
    if (!selectedDay) return t("admin.pickDate", "Pick a date");
    return selectedDay.toLocaleDateString(i18n.resolvedLanguage, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, [selectedDay, i18n.resolvedLanguage, t]);

  const emit = (date: string, time: string) => {
    if (!date) return;
    onChange(`${date}T${time || "09:00"}`);
  };

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) return;
    emit(formatDateToIso(day), parsed.time);
    setCalendarOpen(false);
  };

  const handleTimeChange = (time: string) => {
    const date = parsed.date || formatDateToIso(new Date());
    emit(date, time);
  };

  const disablePast = (day: Date) => {
    if (!disabledBefore) return false;
    const d = new Date(disabledBefore);
    d.setHours(0, 0, 0, 0);
    return day < d;
  };

  return (
    <div className="dtp">
      <div className="dtp-controls">
        <button
          type="button"
          className={`dtp-date-btn${selectedDay ? "" : " is-placeholder"}`}
          onClick={() => setCalendarOpen((open) => !open)}
          aria-expanded={calendarOpen}
        >
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {displayLabel}
        </button>
        <select
          className="dtp-time-select"
          value={parsed.time}
          onChange={(e) => handleTimeChange(e.target.value)}
          required={required}
          aria-label={t("admin.pickTime", "Time")}
        >
          <option value="" disabled>
            {t("admin.pickTime", "Time")}
          </option>
          {TIME_OPTIONS.map((time) => (
            <option key={time} value={time}>
              {time}
            </option>
          ))}
        </select>
      </div>

      {calendarOpen && (
        <div className="dtp-calendar">
          <DayPicker
            mode="single"
            selected={selectedDay}
            onSelect={handleDaySelect}
            disabled={disablePast}
            startMonth={selectedDay ?? new Date()}
            weekStartsOn={0}
            dir={isRtl ? "rtl" : "ltr"}
            className="dtp-day-picker"
          />
        </div>
      )}
      {/* hidden input so native required-validation still works on submit */}
      {required && (
        <input
          className="dtp-hidden-input"
          value={value}
          required
          onChange={() => undefined}
          tabIndex={-1}
          aria-hidden
        />
      )}
    </div>
  );
}

export default DateTimePicker;
